import { useEffect, useState } from 'react';
import { Building2, Loader2, Star, EyeOff, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { OPERATION_LABELS, PROPERTY_TYPE_LABELS } from '../../lib/constants';

export default function AdminProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = () => {
    api
      .get('/admin/properties')
      .then(({ data }) => setProperties(data))
      .catch(() => toast.error('No se pudieron cargar las propiedades'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const patch = async (id, updates) => {
    setUpdating(id);
    try {
      await api.patch(`/admin/properties/${id}`, updates);
      toast.success('Propiedad actualizada.');
      load();
    } catch {
      toast.error('No se pudo actualizar.');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-properties-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">MLS</p>
        <h1 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Building2 className="h-7 w-7 text-cnib-blue" /> Propiedades de la red
        </h1>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-cnib-blue" /></div>
        ) : properties.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-500">No hay propiedades publicadas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm" data-testid="admin-properties-table">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3.5">Propiedad</th>
                  <th className="px-5 py-3.5">Socio</th>
                  <th className="px-5 py-3.5">Operación</th>
                  <th className="px-5 py-3.5">Precio</th>
                  <th className="px-5 py-3.5">Vistas</th>
                  <th className="px-5 py-3.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {properties.map((p) => (
                  <tr key={p.property_id} className="transition-colors hover:bg-slate-50" data-testid={`admin-property-${p.property_id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {p.images?.[0] ? (
                          <img src={p.images[0]} alt="" className="h-11 w-14 rounded-lg object-cover" />
                        ) : (
                          <div className="flex h-11 w-14 items-center justify-center rounded-lg bg-slate-100"><Building2 className="h-4 w-4 text-slate-400" /></div>
                        )}
                        <div className="min-w-0">
                          <p className="max-w-52 truncate font-bold text-slate-800">{p.title}</p>
                          <p className="text-xs text-slate-500">{PROPERTY_TYPE_LABELS[p.property_type]} · {p.city}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-slate-700">{p.owner_name}</p>
                      <p className="font-mono text-xs text-slate-400">{p.owner_member_number}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{OPERATION_LABELS[p.operation]}</td>
                    <td className="px-5 py-4 font-bold text-cnib-blue">US$ {Number(p.price_usd).toLocaleString('es-BO')}</td>
                    <td className="px-5 py-4 text-slate-600">{p.views}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => patch(p.property_id, { featured: !p.featured })}
                          disabled={updating === p.property_id}
                          data-testid={`admin-featured-${p.property_id}`}
                          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${
                            p.featured ? 'bg-amber-500/10 text-amber-600' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          <Star className={`h-3.5 w-3.5 ${p.featured ? 'fill-amber-500 text-amber-500' : ''}`} />
                          {p.featured ? 'Destacada' : 'Destacar'}
                        </button>
                        <button
                          onClick={() => patch(p.property_id, { listed: !p.listed })}
                          disabled={updating === p.property_id}
                          data-testid={`admin-listed-${p.property_id}`}
                          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${
                            p.listed ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'
                          }`}
                        >
                          {p.listed ? <><Eye className="h-3.5 w-3.5" /> Visible</> : <><EyeOff className="h-3.5 w-3.5" /> Oculta</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
